export interface Structure {
  model_url: string;   // Model URL
  model_format: string;   // Model format
  binary: boolean;
}

export const PROVIDER_MAP: Record<string, string> = {
  GDM: 'Google DeepMind' ,
  BFVD: 'Big Fantastic Virus Database (BFVD)',
  ATBC: 'AllTheBacteria',
  NTDX: 'Kinetoplastid Parasites',
  VR3D: 'Viro3D',
  NVDA: 'NVIDIA',
  VADB: 'VAD'
};
export interface EntryData {
  modelEntityId: string;
  gene: string;
  uniprotAccession: string;
  uniprotId: string;
  uniprotDescription: string;
  taxId: number;
  organismScientificName: string;
  sequenceStart: number;
  sequenceEnd: number;
  uniprotStart?: number;
  uniprotEnd?: number;
  uniprotSequence: string;
  sequence:string;
  modelCreatedDate: string;
  latestVersion: number;
  allVersions: [number];
  cifUrl: string;
  bcifUrl: string;
  pdbUrl: string;
  paeDocUrl: string;
  isUniProt: boolean;
  isUniProtReferenceProteome: boolean;
  isUniProtReviewed: boolean;
  amAnnotationsUrl: string;
  globalMetricValue: number;
  fractionPlddtVeryLow: number;
  fractionPlddtLow: number;
  fractionPlddtConfident: number;
  fractionPlddtVeryHigh: number;
  paeImageUrl: string;
  csvAMFileUrl?: string;
  amAnnotationsHg19Url?: string;
  amAnnotationsHg38Url?: string;
  sequenceVersionDate?: string;
  providerId?: string;
  toolUsed?: string;
  otherOrganismScientificNames?: any
  msaUrl?: string;
  plddtDocUrl?: string;
  isComplex?: boolean;
  assemblyType?: string;
}

export interface ProviderData {
  providerName : string,
  toolUsed: string,
  providerUrl: string,
  feedback: string,
  license: string,
  citations: Citations[],
  copyrights: any,
  modelCreatedDate: string
}

export interface Citations {
  description: string,
  url: string,
  category: string
}
export interface PlddtData{
  fractionPlddtVeryLow: number;
  fractionPlddtLow: number;
  fractionPlddtConfident: number;
  fractionPlddtVeryHigh: number;
}

export interface SimilarStructure {
  afdbAccessions : string;
  uniprotDescriptions: string;
  speciesNames: string;
  expanded: boolean;
  targetToShow: string;
}

export interface ClusterData {
  clusterTotal: number;
  clusterMembers : SimilarStructure;
}

export class SummaryText {
  label: string; // Label for the text, e.g. 'Organism'
  text: string; // Text
  italic: boolean; // I.e. true/false
  show_long: boolean; // Show the full text, no matter how long, i.e. true/false
  source: string; // Source of the information, if external, e.g. UniProt
  source_url: string; // Link to the source of information, if external
  isTooltip?: boolean
}

export class SummaryData {
  title: string; // For example 'Q99ZW2'
  subtitle: string; // For example 'CRISPR-associated endonuclease Cas9/Csn1',
  flags: Flag[]; // Flags, for example EC number for enzymes
  text_limit: number; // Number of words displayed; additional words are collapsed by default, except of show_long is true
  texts: SummaryText[];
}

export class Flag {
  name: string; // Flag name
  color: string; // Background color of the flag
  url: string; // URL to external resource, if applicable
  tooltip: string; // Tooltip text
}

export class TedDomain {
  ted_domain_no: number;
  cath_label: string;
  nres_domain: number;
  plddt: number;
  cath_assignment_level: string;
  qscore: number;
  segments: {
    af_start: number;
    af_end: number;
    segment_id: number;
  }[];
}

export interface LlmAnnotation {
  pubmedId: number;
  pmcId: string;
  doi: string;
  primaryCitation: string;
  pdbResidue: number;
  uniprotResidue: number;
  // Not part of the API's own additionalData item — copied down from the parent deposition's
  // `pdbId` by parseLlmAnnotationsResponse, for the CSV download's "PDB ID" column.
  pdbId?: string;
  pdbChain: string;
  uniprotAccession: string;
  sentence: string;
  exact: string;
  aiScore: number;
  // Real API field, sent as a number (e.g. 2018) on the live API as of 2026-09-30; typed loosely
  // since it can still be null — string and number both interpolate fine in the template either way.
  publicationYear: string | number | null;
  authors: string[];
  // 'Y' when the source publication's licence permits sharing the mined sentence text; seen as
  // 'N', '', or null otherwise (copyright-restricted). Anything other than exactly 'Y' must be
  // treated as restricted.
  permittingLicense: string | null;
}

export interface PlddtConfidenceChain {
  name: string;
  label_asym_id: string;
  sequenceStart: number;
  sequenceEnd: number;
}

export interface PlddtResidueData {
  residueNumber: number[];
  confidenceScore: number[];
  // Present on a complex entry's confidence file: residueNumber runs continuously across the
  // WHOLE assembly (one shared file per complex, not per chain), and this describes each
  // chain's own local numbering/name so a consumer can slice out just its segment.
  chains?: PlddtConfidenceChain[];
}

export interface LlmAnnotationsData {
  dataType: string;
  data: LlmAnnotation[];
  // The UniProt accession this batch of annotations was actually fetched for — the API is
  // keyed per-accession, so a heterodimer's two chains never share one payload. Lets consumers
  // detect when the currently loaded data belongs to a different chain than the one on screen.
  uniprotAccession?: string;
}
